import { useMutation } from '@apollo/client/react';
import { UPDATE_EMAILS_MUTATION } from '../../integrations/allkort/graphql/gql/EmailMutations';
import { EmailTypeId, UpdateEmailsResponse } from '../../integrations/allkort/graphql/interface/Emails';

/**
 * useUpdateEmail
 *
 * Custom hook for updating an existing email address on the customer's account.
 * Also triggers a new verification code to be sent to the updated address.
 *
 * @example
 * ```tsx
 * const { updateEmail, loading, error } = useUpdateEmail();
 *
 * const handleUpdate = async () => {
 *   const result = await updateEmail({
 *     emailId: 382355,
 *     address: 'new@example.com',
 *     emailType: 'INFORMATION',
 *     sortOrder: 1,
 *   });
 *   if (result) console.log('Updated:', result);
 * };
 * ```
 */
export const useUpdateEmail = () => {
	const [updateEmailsMutation, { loading, error, data }] = useMutation<UpdateEmailsResponse>(
		UPDATE_EMAILS_MUTATION
	);

	/**
	 * Update an existing email address on the customer's account.
	 * Triggers a new verification code to be sent to the updated address.
	 * @param emailId   - The ID of the existing email to update
	 * @param address   - The new email address
	 * @param emailType - Email type enum (e.g. 'INFORMATION', 'MAIN', etc.)
	 * @param sortOrder - Display sort order, defaults to 1
	 * @returns The updated email list, or null if the mutation failed
	 */
	const updateEmail = async ({
		emailId,
		customerId,
		address,
		emailType,
		sortOrder = 1,
	}: {
		emailId: number
		customerId: number
		address: string
		emailType: EmailTypeId
		sortOrder?: number
	}) => {
		try {
			const result = await updateEmailsMutation({
				variables: { emailId, customerId, address, emailType, sortOrder },
			});

			if (result.error) {
				const message = (result.error as any).graphQLErrors?.[0]?.message ?? result.error.message;
				console.error('[useUpdateEmail] GraphQL error:', message);
				throw new Error(message);
			}

			return result.data?.updateEmails?.email ?? null;
		} catch (err) {
			throw err;
		}
	};

	return { updateEmail, loading, error, data };
};
